import { useRouter } from 'next/router'
import { scrollIntoTheView } from '@/v1/helpers'
import SweetAlert, { SweetAlertResult } from 'sweetalert2'
import { KeyValuePairs } from '@/types/global'

interface ConfirmAlertProps {
  icon?: string
  text?: string
  confirmButtonText?: string
  denyButtonText?: string
}

type IconType = 'success' | 'error' | 'warning' | 'info'

export const usePopupAlerts = () => {
  const router = useRouter()
  const responseChecker = (res: any, returnUrl = '#', defaultMessage = 'Something Went Wrong!') => {
    if (res?.statusCode === 200 && res.data.status) {
      SweetAlert.fire({
        icon: 'success',
        text: res.data.message,
        showConfirmButton: true,
        allowOutsideClick: false,
        timer: 2500
      }).then(function () {
        if (returnUrl && returnUrl !== '#') router.push(returnUrl).then()
      })
    } else if (res?.statusCode === 422) {
      SweetAlert.fire({
        icon: 'error',
        text: res.data.message,
        showConfirmButton: true,
        allowOutsideClick: false
      }).then()
    } else {
      SweetAlert.fire({
        icon: 'error',
        text: res.data.message || defaultMessage,
        showConfirmButton: true,
        allowOutsideClick: false
      }).then()
    }
  }
  const sendNotification = (iconTye: IconType = 'success', message = '', ...rest: any) => {
    const restObject = Object.assign({}, ...rest) // Merge array of objects into a single object

    SweetAlert.fire({
      position: 'center',
      text: message,
      showConfirmButton: true,
      timerProgressBar: true,
      showCloseButton: false,
      allowOutsideClick: false,
      timer: iconTye === 'success' ? 2500 : '',
      icon: `${iconTye}`,
      customClass: {
        actions: 'my-actions',
        cancelButton: 'order-1 right-gap',
        confirmButton: 'order-2',
        denyButton: 'order-3',
        topZIndex: 'height-z-index'
      },
      ...restObject
    }).then()
  }

  const smoothScrollNotification = (iconTye: IconType = 'success', message = '', targetId: string, ...rest: any) => {
    const restObject = Object.assign({}, ...rest) // Merge array of objects into a single object

    SweetAlert.fire({
      position: 'center',
      text: message,
      showConfirmButton: true,
      timerProgressBar: true,
      showCloseButton: false,
      allowOutsideClick: false,
      timer: iconTye === 'success' ? 2500 : '',
      icon: `${iconTye}`,
      customClass: {
        actions: 'my-actions',
        cancelButton: 'order-1 right-gap',
        confirmButton: 'order-2',
        denyButton: 'order-3',
        topZIndex: 'height-z-index'
      },
      ...restObject
    }).then(() => {
      if (targetId) {
        setTimeout(() => {
          scrollIntoTheView(targetId)
        }, 500)
      }
    })
  }

  const errorNotification = (iconTye: IconType = 'error', message = '', ...rest: any) => {
    const restObject = Object.assign({}, ...rest) // Merge array of objects into a single object

    SweetAlert.fire({
      position: 'center',
      text: message,
      showConfirmButton: true,
      timerProgressBar: true,
      showCloseButton: false,
      allowOutsideClick: false,
      timer: iconTye === 'success' ? 2500 : '',
      icon: `${iconTye}`,
      customClass: {
        actions: 'my-actions',
        cancelButton: 'order-1 right-gap',
        confirmButton: 'order-2',
        denyButton: 'order-3',
        topZIndex: 'height-z-index'
      },
      ...restObject
    }).then()
  }

  async function confirmAlert(attributes: ConfirmAlertProps, ...rest: any): Promise<SweetAlertResult> {
    const restObject = Object.assign({}, ...rest) // Merge array of objects into a single object

    const selectedIcon = attributes.icon || 'info'
    const selectedText = attributes.text || 'Do you really want to take this action?'

    return await SweetAlert.fire({
      showDenyButton: true,
      showCancelButton: false,
      allowOutsideClick: false,
      showLoaderOnConfirm: true,
      allowEnterKey: false,
      allowEscapeKey: false,
      confirmButtonText: attributes.confirmButtonText || 'Yes',
      denyButtonText: attributes.denyButtonText || 'No',
      icon: `${selectedIcon}`,
      text: selectedText,
      timer: selectedIcon === 'success' ? 2500 : '',
      customClass: {
        actions: 'my-actions',
        cancelButton: 'order-1 right-gap',
        confirmButton: 'order-2',
        denyButton: 'order-3',
        topZIndex: 'height-z-index'
      },
      ...restObject
    })
  }

  const validationErrorsWithBreak = (errors: KeyValuePairs) => {
    let htmlError = ''
    for (const key in errors) {
      const error = errors[key]
      htmlError += error?.[0] + `<br/>`
    }

    SweetAlert.fire({
      icon: 'error',
      html: htmlError,
      confirmButtonText: 'Ok'
    }).then()

    return false
  }

  const notifyUser = (iconType: IconType = 'success', message = '', ...rest: any) => {
    const restObject = Object.assign({}, ...rest) // Merge array of objects into a single object

    const options = {
      position: 'center',
      icon: iconType,
      showConfirmButton: true,
      timerProgressBar: true,
      showCloseButton: false,
      timer: iconType === 'success' ? 2500 : '',
      customClass: {
        actions: 'my-actions',
        cancelButton: 'order-1 right-gap',
        confirmButton: 'order-2',
        denyButton: 'order-3',
        topZIndex: 'height-z-index'
      },
      text: message,
      ...restObject
    }

    return new Promise((resolve, reject) => {
      SweetAlert.fire(options)
        .then(result => {
          resolve(result)
        })
        .catch(error => {
          reject(error)
        })
    })
  }

  return {
    notifyUser,
    confirmAlert,
    responseChecker,
    sendNotification,
    errorNotification,
    smoothScrollNotification,
    validationErrorsWithBreak
  }
}
